import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  AlertTitle,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { apiService } from '../services/api';

interface SystemHealth {
  status: string;
  database_status: string;
  api_status: string;
  storage_status: string;
  recent_errors: number;
  avg_response_time_ms: number;
  timestamp: string;
  details?: {
    status: string;
    timestamp: string;
    checks: {
      database?: any;
      firebase_auth?: any;
      gemini_api?: any;
      system_resources?: any;
      error?: any;
    };
    endpoints?: any;
    recent_errors?: any;
    avg_response_time_ms?: any;
    system_info?: any;
    dependencies?: any;
  };
}

function SystemHealthPage() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadSystemHealth = async () => {
      try {
        setLoading(true);
        
        // 認証トークンを取得
        console.log('Current user:', currentUser?.email);
        
        if (!currentUser) {
          console.error('No authenticated user in SystemHealth');
          setHealth({
            status: 'error',
            database_status: 'unknown',
            api_status: 'unknown',
            storage_status: 'unknown',
            recent_errors: 0,
            avg_response_time_ms: 0,
            timestamp: new Date().toISOString(),
          });
          setLoading(false);
          return;
        }
        
        console.log('Current user:', currentUser);
        console.log('Is system admin:', currentUser?.is_system_admin);
        console.log('Is super admin:', currentUser?.is_super_admin);
        
        console.log('Making request to system health API');
        
        try {
          const data = await apiService.getSystemHealth() as SystemHealth;
          setHealth(data);
        } catch (error: any) {
          console.error('System health API error:', error);
          
          // エラーの場合でも詳細情報を含める
          setHealth({
            status: 'error',
            database_status: 'error',
            api_status: 'error',
            storage_status: 'unknown',
            recent_errors: 0,
            avg_response_time_ms: 0,
            timestamp: new Date().toISOString(),
            details: {
              status: 'error',
              timestamp: new Date().toISOString(),
              checks: {
                error: {
                  status: 'error',
                  message: error.message || 'API Error',
                  details: {
                    error: error.toString()
                  }
                }
              },
              endpoints: {
                base_url: window.location.origin
              }
            }
          });
        }
      } catch (error) {
        console.error('Failed to load system health:', error);
        // エラーの場合はダミーデータを表示
        setHealth({
          status: 'error',
          database_status: 'unknown',
          api_status: 'unknown',
          storage_status: 'unknown',
          recent_errors: 0,
          avg_response_time_ms: 0,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    // currentUserが存在する場合のみ実行
    if (currentUser) {
      loadSystemHealth();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />;
      case 'degraded':
        return <WarningIcon color="warning" sx={{ fontSize: 40 }} />;
      case 'developing':
        return <WarningIcon color="info" sx={{ fontSize: 40 }} />;
      default:
        return <ErrorIcon color="error" sx={{ fontSize: 40 }} />;
    }
  };

  const getStatusAlert = (status: string) => {
    switch (status) {
      case 'healthy':
        return (
          <Alert severity="success">
            <AlertTitle>システム正常</AlertTitle>
            すべてのサービスが正常に動作しています。
          </Alert>
        );
      case 'degraded':
        return (
          <Alert severity="warning">
            <AlertTitle>パフォーマンス低下</AlertTitle>
            一部のサービスでパフォーマンスの低下が発生しています。
          </Alert>
        );
      default:
        return (
          <Alert severity="error">
            <AlertTitle>システム異常</AlertTitle>
            システムに問題が発生しています。至急確認してください。
          </Alert>
        );
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          システムヘルス
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!health) return null;

  const services = [
    { name: 'データベース', status: health.database_status || 'developing' },
    { name: 'API', status: health.api_status || 'developing' },
    { name: 'ストレージ', status: health.storage_status || 'developing' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          システムヘルス
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {currentUser && (
            <Typography variant="body2" color="text.secondary">
              ログイン中: {currentUser.email}
            </Typography>
          )}
          <Button variant="outlined" onClick={() => window.location.reload()}>
            再読み込み
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>{getStatusAlert(health.status)}</Box>

      <Grid container spacing={3}>
        {services.map((service) => (
          <Grid item xs={12} md={4} key={service.name}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              {getStatusIcon(service.status)}
              <Typography variant="h6" sx={{ mt: 2 }}>
                {service.name}
              </Typography>
              <Typography
                variant="body2"
                color={
                  service.status === 'healthy'
                    ? 'success.main'
                    : service.status === 'degraded'
                    ? 'warning.main'
                    : service.status === 'developing'
                    ? 'info.main'
                    : 'error.main'
                }
              >
                {service.status === 'healthy' ? '正常' : 
                 service.status === 'degraded' ? '低下' : 
                 service.status === 'developing' ? '実装中' : '異常'}
              </Typography>
              {/* サービス詳細情報 */}
              {service.name === 'API' && health.details?.checks?.gemini_api && (
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="caption" color="text.secondary">
                    {health.details.checks.gemini_api.message}
                  </Typography>
                </Box>
              )}
              {service.name === 'データベース' && health.details?.checks?.database && (
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="caption" color="text.secondary">
                    {health.details.checks.database.message}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              パフォーマンス指標
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                平均レスポンス時間
              </Typography>
              <Typography variant="h4">
                {health.avg_response_time_ms} ms
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              エラー情報
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                直近24時間のエラー数
              </Typography>
              <Typography variant="h4" color={health.recent_errors > 10 ? 'error' : 'inherit'}>
                {health.recent_errors}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="body2" color="textSecondary">
              最終更新: {new Date(health.timestamp).toLocaleString('ja-JP')}
            </Typography>
          </Paper>
        </Grid>

        {/* エラー詳細セクション */}
        {health.status === 'error' && health.details?.checks?.error && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
              <Typography variant="h6" gutterBottom>
                エラー詳細
              </Typography>
              {health.details.checks.error.details?.response_status === 403 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>アクセス権限が不足しています</AlertTitle>
                  システムヘルスを表示するには、システム管理者権限が必要です。
                  システムテナント（subdomain: system）に所属し、管理者権限を持つユーザーでログインしてください。
                </Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>メッセージ:</strong> {health.details.checks.error.message}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>試行したURL:</strong> {health.details.checks.error.details?.attempted_url}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>レスポンスステータス:</strong> {health.details.checks.error.details?.response_status}
                </Typography>
                {health.details.checks.error.details?.response_body && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>エラー詳細:</strong>
                    </Typography>
                    <pre style={{ 
                      overflow: 'auto', 
                      backgroundColor: 'rgba(0,0,0,0.1)', 
                      padding: '8px',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      {JSON.stringify(health.details.checks.error.details.response_body, null, 2)}
                    </pre>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* 詳細情報セクション */}
        {health.details && (
          <>
            {/* API エンドポイント一覧 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  APIエンドポイント
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {health.details.endpoints && Object.entries(health.details.endpoints).map(([key, value]) => (
                    <Typography key={key} variant="body2" sx={{ mb: 1 }}>
                      <strong>{key}:</strong> {value as string}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* データベース詳細 */}
            {health.details.checks.database?.details && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    データベース詳細
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>接続:</strong> {health.details.checks.database.details.connection}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>テーブル数:</strong> {health.details.checks.database.details.table_count}
                    </Typography>
                    <Typography variant="body2">
                      <strong>レスポンス時間:</strong> {health.details.checks.database.details.response_time_ms}ms
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Firebase Auth詳細 */}
            {health.details.checks.firebase_auth?.details && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Firebase Auth詳細
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>プロジェクトID:</strong> {health.details.checks.firebase_auth.details.project_id}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>サービスアカウント:</strong> {health.details.checks.firebase_auth.details.service_account}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Gemini API詳細 */}
            {health.details.checks.gemini_api?.details && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Gemini AI詳細
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>モデル:</strong> {health.details.checks.gemini_api.details.model}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>AI機能エンドポイント:</strong>
                    </Typography>
                    {health.details.checks.gemini_api.details.endpoints && Object.entries(health.details.checks.gemini_api.details.endpoints).map(([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                        • {key}: {value as string}
                      </Typography>
                    ))}
                    <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                      <strong>対応機能:</strong>
                    </Typography>
                    {health.details.checks.gemini_api.details.capabilities && health.details.checks.gemini_api.details.capabilities.map((cap: string) => (
                      <Typography key={cap} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                        • {cap}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* システム情報 */}
            {health.details.system_info && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    システム情報
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>プラットフォーム:</strong> {health.details.system_info.platform}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Pythonバージョン:</strong> {health.details.system_info.python_version}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Docker環境:</strong> {health.details.system_info.docker ? 'はい' : 'いいえ'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>環境:</strong> {health.details.system_info.environment}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* 依存サービス */}
            {health.details.dependencies && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    依存サービス
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {Object.entries(health.details.dependencies).map(([service, info]: [string, any]) => (
                      <Grid item xs={12} md={4} key={service}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                            {service}
                          </Typography>
                          <Typography variant="body2" color={info.status === 'configured' ? 'success.main' : 'text.secondary'}>
                            ステータス: {info.status === 'configured' ? '設定済み' : '未設定'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            用途: {info.purpose}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* デバッグ情報 */}
            {process.env.NODE_ENV === 'development' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    デバッグ情報（開発環境のみ）
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>受信データ:</strong>
                    </Typography>
                    <pre style={{ 
                      overflow: 'auto', 
                      backgroundColor: '#f5f5f5', 
                      padding: '16px',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      maxHeight: '400px'
                    }}>
                      {JSON.stringify(health, null, 2)}
                    </pre>
                  </Box>
                </Paper>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
}

export default SystemHealthPage;